import React, { useCallback, useState } from 'react';
import moment from 'moment';

import { getFreeTimeForNewSlot, validSlot } from 'components/slotSchedule/utils';
import HospitalSelect from 'components/complex/HospitalSelect';
import { objectToSelectList, remCss, lang, scssVariables as variables } from 'src/helpers';
import {
  CheckboxesField,
  DatePickerField,
  MCButton,
  MCError,
  TimePickerField,
  View
} from 'components/basic';
import LANG_CONST from 'src/helpers/lang/const';
import mainModel from 'components/slotSchedule/model';
import errorDescriptions from 'src/constants/errors';
import { ExpandAnimationComponent } from 'components/Form';
import SVGIcon, { BASE_ICONS } from 'components/svg';

export const EditSlot = ({ slot, onSaved, onClose, slots = [], showDate = false }) => {
  const hospitalParams = mainModel.getHospitalParams();
  const doctorId = mainModel.state.doctorId;

  const [day, setDay] = useState(slot?.day || slot?.startDate);
  const [startDate, setStartDate] = useState(slot?.startDate);
  const [endDate, setEndDate] = useState(slot?.endDate);
  const [hospital, setHospital] = useState(
    slot
      ? { hospitalId: slot.hospitalId, hospital: slot.hospital, cityAddress: slot.cityAddress }
      : null
  );
  const [types, setTypes] = useState(slot?.appointmentTypes);
  const [invalid, setInvalid] = useState({});
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);

  const save = useCallback(
    (e) => {
      e.preventDefault();
      if (loading) return;
      setLoading(true);
      setInvalid({});

      const start = startDate
        ? moment(day)
            .hours(moment(startDate).hours())
            .minutes(moment(startDate).minutes())
            .valueOf()
        : null;

      const end = endDate
        ? moment(day).hours(moment(endDate).hours()).minutes(moment(endDate).minutes()).valueOf()
        : null;

      const params = {
        id: slot?.id,
        slotId: slot?.id,
        doctorId,
        appointmentTypes: types,
        enabled: true,
        startDate: start,
        endDate: end,
        ...hospital
      };

      const invalid = validSlot(params, slots);
      if (invalid.length > 0) {
        setLoading(false);
        return setInvalid(invalid);
      }

      mainModel.saveSlot(params, (error, data) => {
        if (!error) {
          setSaved(true);
        } else {
          setInvalid({ time: errorDescriptions(error) });
        }
        setLoading(false);
        onSaved(error, data);
      });
    },
    [startDate, endDate, hospital, types, loading, slots, day, doctorId, onSaved, slot?.id]
  );

  return (
    <View className={'mc-b2 mc-p20 mc-flex-20-col'}>
      {showDate && (
        <DatePickerField
          close={false}
          style={{ maxWidth: remCss(220) }}
          styleBox={{ backgroundColor: 'white' }}
          template={'6'}
          icontype={2}
          placeholder={LANG_CONST.date}
          placement={'bottom-center'}
          value={day}
          onChange={(n, v) => {
            setDay(v);
            setSaved(false);
          }}
          invalid={{}}
        />
      )}
      <div className={'mc-flex-20 mc-wrap mc-grow'} style={{ alignItems: 'baseline' }}>
        <div className={'mc-grow'} style={{ minWidth: remCss(110) }}>
          <TimePickerField
            template={5}
            width={'100%'}
            placeholder={lang.t(LANG_CONST.select_time)}
            value={startDate ? moment(startDate) : null}
            onSelect={(value) => {
              setStartDate(value.valueOf());
              setSaved(false);
            }}
          />
        </div>
        <div className={'mc-value-418'}>{lang.t('To')}</div>
        <div className={'mc-grow'} style={{ minWidth: remCss(110) }}>
          <TimePickerField
            template={5}
            width={'100%'}
            placeholder={lang.t(LANG_CONST.select_time)}
            value={endDate ? moment(endDate) : null}
            onSelect={(value) => {
              setEndDate(value.valueOf());
              setSaved(false);
            }}
          />
        </div>

        <div
          className={'mc-flex'}
          style={{
            gap: remCss(15),
            alignItems: 'baseline'
          }}
        >
          <CheckboxesField
            template={'2'}
            list={objectToSelectList(LANG_CONST.constants.appointment_types)}
            data={types}
            onChange={(value) => {
              if (!value.length) return;
              setTypes(value);
              setSaved(false);
            }}
          />

          <div style={{ width: remCss(250) }}>
            {types.includes('in_office') && (
              <HospitalSelect
                value={hospital?.hospitalId}
                placeholder={LANG_CONST.location}
                isClearable={false}
                apiConfig={hospitalParams}
                onChange={(n, v, option) => {
                  setHospital({
                    hospitalId: v,
                    hospital: option.item.hospital,
                    cityAddress: option.item.city
                  });
                  setSaved(false);
                }}
              />
            )}
          </div>
        </div>
      </div>
      <View style={{ flexDirection: 'row', gap: remCss(20) }}>
        <View style={{ flex: 1 }}>
          {invalid.time && (
            <MCError error={invalid.time} label={LANG_CONST.time} mcTextProps={{ primary: true }} />
          )}
          {invalid.location && (
            <MCError
              error={invalid.location}
              label={LANG_CONST.location}
              mcTextProps={{ primary: true }}
            />
          )}
        </View>
        <View>
          <MCButton
            primary
            light
            small
            radius
            disabled={loading}
            style={{ width: remCss(80) }}
            label={LANG_CONST.buttons.cancel}
            onClick={onClose}
          />
        </View>
        <View>
          <MCButton
            label={saved ? LANG_CONST.saved : LANG_CONST.buttons.save}
            primary
            dark
            small
            radius
            style={{ width: remCss(80) }}
            disabled={loading}
            loading={loading}
            onClick={save}
          />
        </View>
      </View>
    </View>
  );
};

export const AddSlotPanel = ({
  day,
  openAdd,
  setOpenAdd,
  addHour,
  onSaved,
  slots = [],
  showDate = false
}) =>
  mainModel.canModify() && (
    <>
      <MCButton
        dark
        medium
        radius
        onClick={() => {
          setOpenAdd((o) => !o);
        }}
      >
        <span>{lang.t(LANG_CONST.buttons.add_slot)}</span>
        <span
          className={'mc-rotate ' + (openAdd ? 'mc-rotate-180' : '')}
          style={{
            height: remCss(30),
            margin: `${remCss(-5)}px ${remCss(-5)}px  ${remCss(-5)}px 0`
          }}
        >
          <SVGIcon
            iconname={BASE_ICONS.DOWN}
            width={30}
            padding={1}
            fill={variables['colors-mc-white']}
          />
        </span>
      </MCButton>
      <ExpandAnimationComponent
        show={openAdd}
        dropdown={true}
        style={{
          position: 'absolute',
          width: '100%',
          height: 'auto',
          top: 0,
          left: 0,
          zIndex: 3,
          backgroundColor: 'white',
          boxShadow: `0 ${remCss(15)}px ${remCss(15)}px rgba(0, 0, 0, .05)`
        }}
      >
        <EditSlot
          slot={{
            appointmentTypes: ['virtual'],
            ...(openAdd ? getFreeTimeForNewSlot(slots, day, addHour || 9) : {})
          }}
          showDate={showDate}
          onSaved={onSaved}
          doctorId={mainModel.state.doctorId}
          hospitalParams={mainModel.getHospitalParams()}
          onClose={() => setOpenAdd(false)}
          slots={slots}
        />
      </ExpandAnimationComponent>
    </>
  );
